import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';

const AuthContext = createContext();

// ✅ ADICIONADO: Export do AuthContext
export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('❌ useAuth: AuthContext não encontrado. Verifique se o componente está dentro do AuthProvider.');
    return null;
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [companyModulos, setCompanyModulos] = useState(undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        console.log('🔍 AuthContext: onAuthStateChanged triggered', firebaseUser ? 'User logged in' : 'User logged out');
        
        if (firebaseUser) {
          console.log('🔍 AuthContext: Firebase user UID:', firebaseUser.uid);
          console.log('🔍 AuthContext: Firebase user email:', firebaseUser.email);
          
          // Validar se o UID existe
          if (!firebaseUser.uid) {
            console.error('❌ AuthContext: UID do usuário está undefined');
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }

          try {
            // Buscar perfil completo do usuário via Cloud Function com retry
            console.log('🔍 AuthContext: Chamando getUser com UID:', firebaseUser.uid);
            const getUser = httpsCallable(functions, 'getUser');
            
            const maxRetries = 3;
            let profile = null;
            let lastError = null;
            
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                console.log(`🔍 AuthContext: Tentativa ${attempt}/${maxRetries} de getUser`);
                
                // Chamar a função com o parâmetro correto
                const result = await getUser({ 
                  userId: firebaseUser.uid 
                });
                
                console.log('🔍 AuthContext: Resultado da getUser:', result);
                console.log('🔍 AuthContext: result.data:', result.data);
                
                if (result.data && result.data.success) {
                  profile = result.data.data;
                  console.log('✅ AuthContext: Perfil do usuário carregado:', profile);
                  break; // Sucesso, sair do loop
                } else {
                  console.error('❌ AuthContext: Erro na resposta da getUser:', result.data);
                  lastError = new Error(result.data?.message || 'Erro na resposta do servidor');
                  break; // Erro de resposta, não tentar novamente
                }
              } catch (functionError) {
                console.error(`❌ AuthContext: Erro ao chamar getUser (tentativa ${attempt}):`, functionError);
                lastError = functionError;
                
                // Se não for erro de rede, não tentar novamente
                if (!functionError.message.includes('network') && 
                    !functionError.message.includes('timeout') &&
                    functionError.code !== 'functions/unavailable') {
                  break;
                }
                
                // Se for a última tentativa, não esperar
                if (attempt < maxRetries) {
                  console.log(`⏳ AuthContext: Aguardando ${attempt} segundo antes da próxima tentativa...`);
                  await new Promise(resolve => setTimeout(resolve, attempt * 1000));
                }
              }
            }
            
            if (profile) {
              setUser(firebaseUser);
              setUserProfile(profile);
            } else {
              console.error('❌ AuthContext: Todas as tentativas de getUser falharam');
              
              // Criar perfil básico em caso de erro
              const basicProfile = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                nome: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                funcao: 'usuario',
                status: 'ativo',
                empresaId: 'default',
                data_criacao: new Date().toISOString(),
                ultimo_login: new Date().toISOString()
              };
              
              console.log('⚠️ AuthContext: Usando perfil básico devido ao erro:', basicProfile);
              setUser(firebaseUser);
              setUserProfile(basicProfile);
            }
          } catch (functionError) {
            console.error('❌ AuthContext: Erro geral ao chamar getUser:', functionError);
            
            // Criar perfil básico em caso de erro
            const basicProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              nome: firebaseUser.displayName || firebaseUser.email.split('@')[0],
              funcao: 'usuario',
              status: 'ativo',
              empresaId: 'default',
              data_criacao: new Date().toISOString(),
              ultimo_login: new Date().toISOString()
            };
            
            console.log('⚠️ AuthContext: Usando perfil básico devido ao erro geral:', basicProfile);
            setUser(firebaseUser);
            setUserProfile(basicProfile);
          }
        } else {
          console.log('🔍 AuthContext: Usuário deslogado');
          setUser(null);
          setUserProfile(null);
          setCompanyModulos(undefined);
        }
      } catch (error) {
        console.error('❌ AuthContext: Erro geral no processamento:', error);
        setUser(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!userProfile) return undefined;
    if (userProfile.funcao === 'superadmin') {
      setCompanyModulos(null);
      return undefined;
    }
    const id = userProfile.id_empresa || userProfile.empresaId;
    if (!id || id === 'default') {
      setCompanyModulos({});
      return undefined;
    }
    let cancel = false;
    (async () => {
      try {
        const getCompany = httpsCallable(functions, 'getCompany');
        const result = await getCompany({ companyId: id });
        const data = result.data?.data || result.data || {};
        const modulos = data.modulos && typeof data.modulos === 'object' ? data.modulos : {};
        if (data.modulo_armazenamento === false && !Object.prototype.hasOwnProperty.call(modulos, 'armazenamento')) {
          modulos.armazenamento = false;
          modulos.operacao_armazem = false;
        }
        if (!cancel) setCompanyModulos(modulos);
      } catch (err) {
        console.error('AuthContext: falha ao carregar módulos da empresa', err);
        if (!cancel) setCompanyModulos({});
      }
    })();
    return () => {
      cancel = true;
    };
  }, [userProfile]);

  const login = async (email, password) => {
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        setLoading(true);
        console.log(`🔍 AuthContext: Tentativa ${attempt}/${maxRetries} de login com email:`, email);
        
        const result = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ AuthContext: Login Firebase bem-sucedido:', result.user.uid);
        
        // O perfil será carregado automaticamente pelo onAuthStateChanged
        return { success: true, user: result.user };
      } catch (error) {
        console.error(`❌ AuthContext: Erro no login (tentativa ${attempt}):`, error);
        lastError = error;
        
        // Se não for erro de rede, não tentar novamente
        if (error.code !== 'auth/network-request-failed' && 
            error.code !== 'auth/too-many-requests' &&
            !error.message.includes('network')) {
          break;
        }
        
        // Se for a última tentativa, não esperar
        if (attempt < maxRetries) {
          console.log(`⏳ AuthContext: Aguardando ${attempt * 2} segundos antes da próxima tentativa...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 2000));
        }
      } finally {
        if (attempt === maxRetries) {
          setLoading(false);
        }
      }
    }
    
    // Se chegou aqui, todas as tentativas falharam
    let message = 'Erro ao fazer login';
    
    switch (lastError.code) {
      case 'auth/user-not-found':
        message = 'Usuário não encontrado';
        break;
      case 'auth/wrong-password':
        message = 'Senha incorreta';
        break;
      case 'auth/invalid-email':
        message = 'Email inválido';
        break;
      case 'auth/user-disabled':
        message = 'Usuário desabilitado';
        break;
      case 'auth/too-many-requests':
        message = 'Muitas tentativas. Tente novamente mais tarde';
        break;
      case 'auth/invalid-credential':
        message = 'Credenciais inválidas. Verifique email e senha';
        break;
      case 'auth/network-request-failed':
        message = 'Erro de conexão. Verifique sua internet e tente novamente';
        break;
      default:
        message = lastError.message || 'Erro de conexão com o servidor';
    }
    
    return { success: false, message };
  };

  const logout = async () => {
    try {
      setLoading(true);
      console.log('🔍 AuthContext: Fazendo logout');
      
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      
      console.log('✅ AuthContext: Logout bem-sucedido');
      return { success: true };
    } catch (error) {
      console.error('❌ AuthContext: Erro no logout:', error);
      return { success: false, message: 'Erro ao fazer logout' };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      console.log('🔍 AuthContext: Tentando registrar usuário:', userData.email);
      
      // Criar usuário via Cloud Function (que também cria no Firebase Auth)
      const createUser = httpsCallable(functions, 'createUser');
      const result = await createUser(userData);
      
      if (result.data.success) {
        console.log('✅ AuthContext: Usuário criado via Cloud Function');
        // Fazer login automaticamente após registro
        const loginResult = await login(userData.email, userData.password);
        return loginResult;
      } else {
        console.error('❌ AuthContext: Erro ao criar usuário:', result.data.message);
        return { success: false, message: result.data.message };
      }
    } catch (error) {
      console.error('❌ AuthContext: Erro no registro:', error);
      return { success: false, message: 'Erro ao criar usuário' };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      console.log('🔍 AuthContext: Enviando email de recuperação para:', email);
      await sendPasswordResetEmail(auth, email, {
        url: 'https://regulamais-463618.web.app/app/login',
        handleCodeInApp: false
      });
      console.log('✅ AuthContext: Email de recuperação enviado com sucesso');
      return { success: true, message: 'Email de recuperação enviado com sucesso' };
    } catch (error) {
      console.error('❌ AuthContext: Erro ao enviar email de recuperação:', error);
      
      let errorMessage = 'Erro ao enviar email de recuperação';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Email não encontrado. Verifique se o email está correto.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido. Por favor, digite um email válido.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Muitas tentativas. Tente novamente mais tarde.';
      }
      
      return { success: false, message: errorMessage };
    }
  };

  const isRole = (roleToCheck) => {
    const hasRole = userProfile && userProfile.funcao === roleToCheck;
    console.log('🔍 AuthContext: Verificando role:', roleToCheck, 'Resultado:', hasRole);
    return hasRole;
  };

  const hasPermission = (permission) => {
    if (!userProfile) {
      console.log('🔍 AuthContext: Sem perfil, negando permissão:', permission);
      return false;
    }
    
    const permissions = {
      superadmin: ['all'],
      gerente: [
        'view_dashboard', 'manage_suppliers', 'manage_products', 
        'manage_documents', 'manage_receivings', 'manage_marketplace',
        'manage_users', 'view_reports', 'manage_settings',
        'view_stock', 'manage_stock', // ✅ Estoque
        'manage_quotations', // ✅ Cotações
        'manage_production', 'view_production_products', 'view_production_nonconformities',
        'manage_equipment', 'view_equipment_dashboard', 'manage_equipment_types',
        'manage_setores', 'view_setores',
      ],
      administrador: [
        'view_dashboard', 'manage_suppliers', 'manage_products',
        'manage_documents', 'manage_users', 'view_reports', 'manage_settings',
        'view_stock', 'manage_stock', // ✅ Estoque
        'manage_quotations', // ✅ Cotações
        'manage_production', 'view_production_products', 'view_production_nonconformities',
        'manage_equipment', 'view_equipment_dashboard',
        'manage_setores', 'view_setores',
      ],
      admin: [ // ✅ CORREÇÃO: Adicionar permissões para 'admin'
        'view_dashboard', 'view_suppliers', 'manage_suppliers', 'view_products', 'manage_products',
        'view_documents', 'manage_documents', 'view_users', 'manage_users', 'view_reports', 'manage_settings',
        'view_stock', 'manage_stock', // ✅ Estoque
        'manage_quotations', // ✅ Cotações
        'manage_production', 'view_production_products', 'view_production_nonconformities',
        'manage_equipment', 'view_equipment_dashboard',
        'manage_setores', 'view_setores',
      ],
      recebedor: [
        'view_dashboard', 'view_suppliers', 'view_products',
        'view_documents', 'manage_receivings'
      ],
      qualidade: [
        'view_dashboard', 'view_suppliers', 'manage_suppliers', 'view_products', 'manage_products',
        'view_documents', 'manage_documents', 'manage_receivings',
        'view_stock', // Estoque (somente visualização)
        'view_production_nonconformities'
      ],
      producao: [ // ✅ NOVO PERFIL: Produção
        'view_dashboard', 'view_products', 'view_suppliers',
        'manage_production', 'view_stock', 'view_production_products', 'view_production_nonconformities'
      ],
      manutencao: [
        'view_dashboard',
        'manage_equipment',
        'view_equipment_dashboard',
      ],
      fornecedor_equipamentos: [
        'view_dashboard',
        'view_own_data',
        'manage_equipment_marketplace',
      ],
      fornecedor: [
        'view_dashboard', 'view_documents', 'manage_marketplace',
        'view_own_data',
        'view_products', 'manage_products', // ✅ Visualizar e criar matérias-primas
        'view_quotations', 'manage_quotations' // ✅ SPRINT 5: Responder cotações
      ],
      usuario: [
        'view_dashboard'
      ]
    };

    const userPermissions = permissions[userProfile.funcao] || [];
    const hasAccess = userPermissions.includes('all') || userPermissions.includes(permission);
    
    console.log('🔍 AuthContext: Verificando permissão:', permission, 'Role:', userProfile.funcao, 'Resultado:', hasAccess);
    return hasAccess;
  };

  const getEmpresaId = () => {
    // ✅ CORREÇÃO: Se for fornecedor, usar id_fornecedor
    let empresaId = 'default';
    
    if (userProfile?.funcao === 'fornecedor' || userProfile?.funcao === 'fornecedor_equipamentos') {
      empresaId =
        userProfile?.id_fornecedor_equipamentos ||
        userProfile?.id_fornecedor ||
        userProfile?.supplier_id ||
        'default';
      console.log('🔍 AuthContext: Fornecedor - usando id_fornecedor:', empresaId);
    } else {
      empresaId = userProfile?.id_empresa || userProfile?.empresaId || 'default';
      console.log('🔍 AuthContext: Empresa - usando id_empresa:', empresaId);
    }
    
    return empresaId;
  };

  const getUserData = () => {
    if (!user) {
      console.log('🔍 AuthContext: getUserData - sem user');
      return {
        uid: null,
        email: null,
        nome: null,
        funcao: null,
        empresaId: 'default',
        status: null,
        displayName: null,
        photoURL: null,
        emailVerified: false,
        createdAt: null,
        lastLogin: null
      };
    }
    
    // Verificação adicional de segurança
    if (!userProfile && !user) {
      console.log('🔍 AuthContext: getUserData - sem user e sem userProfile');
      return {
        uid: null,
        email: null,
        nome: null,
        funcao: null,
        empresaId: 'default',
        status: null,
        displayName: null,
        photoURL: null,
        emailVerified: false,
        createdAt: null,
        lastLogin: null
      };
    }
    
    if (!userProfile) {
      console.log('🔍 AuthContext: getUserData - sem userProfile, retornando dados básicos');
      // Retornar dados básicos do Firebase user
      return {
        uid: user.uid,
        email: user.email,
        nome: user.displayName || user.email.split('@')[0],
        funcao: 'usuario', // Função padrão
        empresaId: 'default',
        status: 'ativo',
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        createdAt: user.metadata?.creationTime,
        lastLogin: user.metadata?.lastSignInTime
      };
    }
    
    // Verificar se userProfile tem função válida
    if (!userProfile.funcao) {
      console.log('🔍 AuthContext: getUserData - userProfile sem função, usando função padrão');
      userProfile.funcao = 'usuario'; // Função padrão
    }
    
    const userData = {
      uid: user.uid,
      email: user.email,
      nome: userProfile.nome,
      funcao: userProfile.funcao,
      empresaId: getEmpresaId(),
      status: userProfile.status,
      displayName: userProfile.nome,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      createdAt: userProfile.data_criacao,
      lastLogin: userProfile.ultimo_login
    };
    
    console.log('🔍 AuthContext: userProfile.funcao:', userProfile.funcao);
    console.log('🔍 AuthContext: userData.funcao:', userData.funcao);
    
    console.log('🔍 AuthContext: getUserData:', userData);
    console.log('🔍 AuthContext: getUserData.funcao:', userData.funcao);
    return userData;
  };

  // Garantir que userData sempre seja um objeto válido
  const currentUserData = getUserData() || {
    uid: null,
    email: null,
    nome: null,
    funcao: 'usuario', // Função padrão
    empresaId: 'default',
    status: 'ativo',
    displayName: null,
    photoURL: null,
    emailVerified: false,
    createdAt: null,
    lastLogin: null
  };
  
  // Garantir que sempre tenha uma função válida
  if (!currentUserData.funcao) {
    currentUserData.funcao = 'usuario';
  }
  
  console.log('🔍 AuthContext: currentUserData final:', currentUserData);

  const value = {
    // Estados
    user: user,
    userProfile,
    loading,
    
    // Funções de autenticação
    login,
    logout,
    register,
    resetPassword,
    
    // Funções de autorização
    isRole,
    hasPermission,
    getEmpresaId,
    
    // Dados do usuário
    getUserData,
    
    // ✅ CORREÇÃO: companyId sempre calculado dinamicamente
    get companyId() {
      return getEmpresaId();
    },
    
    // ✅ CORREÇÃO: userData como valor calculado uma vez
    userData: currentUserData,
    
    userRole: userProfile?.funcao,
    companyModulos
  };

  // ✅ DEBUG: Log detalhado do companyId
  console.log('🔍 AuthContext: companyId debug:', {
    userProfile: userProfile,
    empresaId: userProfile?.empresaId,
    id_empresa: userProfile?.id_empresa,
    companyId: getEmpresaId()
  });

  console.log('🔍 AuthContext: Provider value:', {
    hasUser: !!value.user,
    hasProfile: !!userProfile,
    loading,
    userRole: userProfile?.funcao,
    userData: value.userData,
    userDataFuncao: value.userData?.funcao
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

